import "../../assets/css/login.scss";
import Header from "../../components/header";
import characters from "../../assets/images/charactors.png";
import lockSvg from "../../assets/images/icons/lock.svg";
import eyeShowSvg from "../../assets/images/icons/eye_show.svg";
import eyeHideSvg from "../../assets/images/icons/eye_hide.svg";
import googleBlackSvg from "../../assets/images/icons/google_black.svg";
import appleBlackSvg from "../../assets/images/icons/apple_black.svg";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { login, verifySocialLogin } from "../../api/auth";

// 아이디 저장 관련 localStorage key
const SAVED_ID_KEY = "savedId";
const IS_ID_SAVE_KEY = "_isIdSave";

function LoginPage() {
  const navigate = useNavigate();

  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isIdSave, setIsIdSave] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // 저장된 아이디 불러오기
  useEffect(() => {
    const savedFlag = localStorage.getItem(IS_ID_SAVE_KEY) === "true";
    setIsIdSave(savedFlag);
    if (savedFlag) {
      const savedId = localStorage.getItem(SAVED_ID_KEY) || "";
      setUserId(savedId);
    }

    const handleAppleRedirect = async () => {
      const hash = window.location.hash;

      // URL에서 id_token이나 code 추출 (fragment 방식일 경우)
      const params = new URLSearchParams(
        window.location.hash.replace("#", "?")
      );
      const idToken = params.get("id_token");

      if (idToken) {
        setIsLoading(true);
        try {
          // 백엔드 검증 API 호출
          const res = await verifySocialLogin("APPLE", idToken, "");
          if (res.success) {
            if (res.data?.accessToken) {
              navigate("/coupon_box", { replace: true });
            } else {
              setError("회원가입이 필요합니다.");
            }
          } else {
            setError(res.error?.message || "Apple 로그인 검증 실패");
          }
        } catch (e) {
          console.error(e);
          setError("로그인 처리 중 오류가 발생했습니다.");
        } finally {
          setIsLoading(false);
        }
      }
    };

    handleAppleRedirect();
  }, []);

  // 아이디 저장 처리
  const handleIdSaveToggle = (checked) => {
    setIsIdSave(checked);
    if (checked) {
      localStorage.setItem(IS_ID_SAVE_KEY, "true");
      localStorage.setItem(SAVED_ID_KEY, userId);
    } else {
      localStorage.setItem(IS_ID_SAVE_KEY, "false");
      localStorage.removeItem(SAVED_ID_KEY);
    }
  };

  const handleUserIdChange = (e) => {
    setUserId(e.target.value);
    if (isIdSave) {
      localStorage.setItem(SAVED_ID_KEY, e.target.value);
    }
  };

  // 유효성 검사
  const validate = () => {
    if (!userId.trim()) {
      setError("아이디를 입력해주세요.");
      return false;
    }
    if (!/^[a-zA-Z0-9]{3,15}$/.test(userId)) {
      setError("아이디는 영문/숫자 3~15자로 입력해주세요.");
      return false;
    }
    if (!password) {
      setError("비밀번호를 입력해주세요.");
      return false;
    }
    if (
      !/^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]{6,12}$/.test(password)
    ) {
      setError("비밀번호는 영문/숫자/특수문자 6~12자로 입력해주세요.");
      return false;
    }
    return true;
  };

  // 아이디/비밀번호 로그인
  const handleLogin = async () => {
    setError("");
    if (!validate()) return;

    setIsLoading(true);
    try {
      const res = await login(userId, password);

      if (res.success) {
        navigate("/coupon_box", { replace: true });
      } else {
        setError(res.error?.message || "로그인에 실패했습니다.");
      }
    } catch (e) {
      console.error(e);
      setError("오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  // Google 로그인 (Google Identity Services 사용)
  const handleGoogleLogin = () => {
    if (!window.google) {
      alert("Google 로그인을 사용할 수 없습니다.");
      return;
    }

    window.google.accounts.id.initialize({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      callback: async (response) => {
        setIsLoading(true);
        try {
          const res = await verifySocialLogin("GOOGLE", response.credential);
          if (res.success) {
            if (res.data?.accessToken) {
              navigate("/coupon_box", { replace: true });
            } else {
              setError(res.data?.message || "Google 로그인에 실패했습니다.");
            }
          } else {
            setError(res.error?.message || "Google 로그인에 실패했습니다.");
          }
        } catch (e) {
          console.error(e);
          setError("Google 로그인 중 오류가 발생했습니다.");
        } finally {
          setIsLoading(false);
        }
      },
    });

    window.google.accounts.id.prompt();
  };

  // Apple 로그인 (Sign In with Apple JS SDK 사용)
  const handleAppleLogin = () => {
    const APPLE_AUTH_URL = "https://appleid.apple.com/auth/authorize";

    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_APPLE_CLIENT_ID, // 반드시 Service ID 사용
      redirect_uri: window.location.origin + "/login", // 등록한 Return URL
      response_type: "code id_token",
      // scope: "name email",
      response_mode: "fragment", // 팝업이 아닌 경우 form_post 권장
      state: "origin:web",
    });

    // SDK 없이 직접 애플 로그인 페이지로 이동시킵니다.
    window.location.href = `${APPLE_AUTH_URL}?${params.toString()}`;
  };

  const handleFindPassword = (e) => {
    e.preventDefault(); // 기본 링크 이동 방지

    const userAgent = navigator.userAgent.toLowerCase();

    // 플레이스토어 및 앱스토어 주소 (실제 앱의 ID나 주소로 변경 필요)
    const PLAY_STORE_URL =
      "https://play.google.com/store/apps/details?id=com.aslo.smartview.donga.kids";
    const APP_STORE_URL = "https://apps.apple.com/app/6738370393";

    if (/iphone|ipad|ipod/.test(userAgent)) {
      // iOS인 경우
      window.location.href = APP_STORE_URL;
    } else if (/android/.test(userAgent)) {
      // 안드로이드인 경우
      window.location.href = PLAY_STORE_URL;
    } else {
      // PC 등 기타 환경일 경우 알림 또는 기본 동작
      alert("비밀번호 찾기는 모바일 앱에서 가능합니다.");
    }
  };

  return (
    <div className="login">
      <div style={{ backgroundColor: "#FFF" }}>
        <Header />
      </div>

      <h1 className="login-title">
        아이의 생각을 키우는 어린이 뉴스 앱, 키즈다
      </h1>

      <div className="login-card">
        <div className="card-header">
          <div className="lock-icon">
            <img src={lockSvg} alt="" />
          </div>
        </div>

        <div className="card-body">
          {error && (
            <p
              className="login-error"
              style={{ color: "red", marginBottom: "8px", fontSize: "13px" }}
            >
              {error}
            </p>
          )}

          <div className="field-group">
            <label className="field-label">아이디</label>
            <div className="input-wrap">
              <input
                type="text"
                placeholder="영문/숫자 3~15자"
                value={userId}
                onChange={handleUserIdChange}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="field-group">
            <div className="field-row">
              <label className="field-label">비밀번호</label>
              <a
                href="#"
                className="find-password"
                onClick={handleFindPassword}
              >
                비밀번호 찾기
              </a>
            </div>
            <div className="input-wrap">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="영문/숫자/특수문자 6~12자"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
              />
              <button
                type="button"
                className="toggle-pw"
                aria-label="비밀번호 보기"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                <img src={showPassword ? eyeHideSvg : eyeShowSvg} alt="" />
              </button>
            </div>
          </div>

          <label className="remember-id">
            <input
              type="checkbox"
              checked={isIdSave}
              onChange={(e) => handleIdSaveToggle(e.target.checked)}
            />
            <span>아이디 기억하기</span>
          </label>

          <button
            type="button"
            className="btn-login"
            onClick={handleLogin}
            disabled={isLoading}
          >
            {isLoading ? "로그인 중..." : "로그인"}
          </button>

          <div style={{ textAlign: "center", marginBottom: "8px" }}>
            <button type="button" className="another-login">
              다른 로그인 방식
            </button>
          </div>

          <div className="divider"></div>

          <div className="social-login">
            <button
              type="button"
              className="btn-social"
              onClick={handleGoogleLogin}
              disabled={isLoading}
            >
              <img src={googleBlackSvg} alt="Google" />
              Google
            </button>
            <button
              type="button"
              className="btn-social"
              onClick={handleAppleLogin}
              disabled={isLoading}
            >
              <img src={appleBlackSvg} alt="Apple" />
              Apple
            </button>
          </div>

          <p className="signup-notice">
            회원가입은 키즈다 앱에서만 가능합니다.
          </p>
        </div>
      </div>

      <div className="login-characters">
        <img src={characters} alt="키즈다 크루 캐릭터들" />
      </div>
    </div>
  );
}

export default LoginPage;
